import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { scrollToId } from "./Nav";
import VoiceButton from "./VoiceButton";

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { duration: 0.95, delay: 0.2 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
});

const fadeAnim = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

const chips = [
  { label: "Veille du marché", pos: "-left-3 sm:-left-8 top-10", delay: "0s" },
  { label: "Réponse 24/7", pos: "-right-2 sm:-right-6 top-1/3", delay: "1.2s" },
  { label: "Agenda rempli", pos: "left-8 bottom-12", delay: "2.1s" },
];

const bars = [0.9, 0.5, 1, 0.6, 0.85];

const GiuliaVisual = () => (
  <div className="relative w-full max-w-[440px] mx-auto aspect-square" data-testid="giulia-visual-placeholder">
    <div className="absolute inset-0 rounded-full border border-dashed border-viol/25 animate-spinSlow" style={{ transformBox: "view-box", transformOrigin: "center" }} />
    <div className="absolute inset-[11%] rounded-full border border-viol/10 animate-spinSlower" style={{ transformBox: "view-box", transformOrigin: "center" }}>
      <span className="absolute top-0 left-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-viol shadow-[0_0_16px_rgba(139,92,246,0.9)]" />
    </div>
    <div className="absolute inset-[24%] rounded-full bg-gradient-to-br from-iris-deep/80 via-viol to-iris-deep/80 blur-2xl animate-pulseGlow" />
    <div className="absolute inset-[26%] rounded-full bg-gradient-to-br from-iris-deep via-viol to-iris-deep flex items-center justify-center shadow-[0_0_80px_rgba(139,92,246,0.45)] overflow-hidden">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.85) 0%, transparent 40%)" }} />
      <div className="flex items-end gap-1.5 h-14" aria-hidden="true">
        {bars.map((h, i) => (
          <span
            key={i}
            className="w-1.5 rounded-full bg-white/90 origin-bottom animate-wave"
            style={{ height: `${h * 100}%`, animationDelay: `${i * 0.14}s` }}
          />
        ))}
      </div>
    </div>
    {chips.map((c) => (
      <span
        key={c.label}
        className={`absolute ${c.pos} glass rounded-full px-3.5 py-1.5 font-mono text-[11px] text-viol-pale whitespace-nowrap animate-float`}
        style={{ animationDelay: c.delay }}
      >
        {c.label}
      </span>
    ))}
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useTransform(my, [-0.5, 0.5], [7, -7]);
  const rotateY = useTransform(mx, [-0.5, 0.5], [-7, 7]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden pt-28 sm:pt-32 pb-20 sm:pb-28 noise"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
      }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(139,92,246,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.07) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
        }}
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-8 items-center">
        <motion.div style={{ y: yText }}>
          <motion.p
            {...fadeAnim(0.05)}
            className="font-mono text-xs sm:text-sm uppercase tracking-[0.3em] text-viol-soft mb-6"
          >
            GiulIATeck — IA opérateur pour TOUS
          </motion.p>

          <h1 className="font-display font-extrabold leading-[1.03] tracking-tight text-[clamp(2.3rem,5.4vw,4rem)] text-white">
            <span className="block overflow-hidden pb-0.5">
              <motion.span className="block" {...lineAnim(0)}>
                Giulia amène des clients
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-0.5">
              <motion.span className="block" {...lineAnim(1)}>
                à votre entreprise
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(2)}>
                <span className="text-grad">et la gère avec vous.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p {...fadeAnim(0.55)} className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-mist">
            Elle surveille votre marché, crée vos contenus et vos pubs, répond à
            vos prospects 24/7 et remplit votre agenda.
          </motion.p>

          <motion.p {...fadeAnim(0.7)} className="mt-3 font-display font-bold text-xl sm:text-2xl text-white">
            Vous validez, <span className="text-grad">elle exécute.</span>
          </motion.p>

          <motion.div {...fadeAnim(0.85)} className="mt-7 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-demo"
              onClick={() => scrollToId("demo")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-iris-deep to-viol px-7 py-3.5 text-base font-medium text-white shadow-[0_0_32px_rgba(139,92,246,0.4)] transition-all duration-300 hover:shadow-[0_0_48px_rgba(139,92,246,0.6)] hover:-translate-y-0.5"
            >
              Demander une démo
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              href="#cycle"
              data-testid="hero-link-cycle"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("cycle");
              }}
              className="text-sm text-mist hover:text-white underline underline-offset-8 decoration-viol/40 hover:decoration-viol transition-all duration-200"
            >
              Découvrir le cycle
            </a>
          </motion.div>

          <motion.div {...fadeAnim(1.0)} className="mt-5">
            <VoiceButton
              voice="giulia"
              text="Bonjour, moi c'est Giulia. Je surveille votre marché, je crée vos contenus et vos pubs, je réponds à vos prospects 24 heures sur 24, et je remplis votre agenda. Vous validez, j'exécute."
              label="Écouter Giulia se présenter"
              testId="hero-voice-btn"
            />
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yVisual }} className="relative">
          <motion.div
            style={{ rotateX, rotateY, transformPerspective: 900 }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            <GiuliaVisual />
          </motion.div>
          <p className="mt-6 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-mist/60">
            Visuel de présentation — portrait définitif à venir
          </p>
        </motion.div>
      </div>

      <motion.div
        {...fadeAnim(1.3)}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:block text-mist/50"
        aria-hidden="true"
      >
        <ChevronDown size={22} className="animate-bounce" />
      </motion.div>
    </section>
  );
};

export default Hero;
